import React from 'react'
import { Button, Empty, Input, Radio, Space, Table } from 'antd'
import type { TableColumnsType } from 'antd'
import {
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined,
    UploadOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'

import {
    FilterActions,
    FilterBar,
    FilterField,
} from '../../../components/common/FilterBar'
import {
    buildPaginatedListPagination,
    PaginatedListContainer,
    PaginatedListCard,
    PaginatedListToolbar,
    PAGINATED_LIST_TABLE_SIZE,
} from '../../../components/common/PaginatedListTable'
import type { ModelVersionSummary } from '../../../types/modelManagement'
import type {
    DisplayCategoryRow,
    ModelConfigurationRow,
    ModelScopeTabKey,
    ProjectConfigRecordRow,
} from './pageConfig'
import {
    BINDING_TABLE_SCROLL_X,
    MODEL_CONFIGURATION_TABLE_SCROLL_X,
    MODEL_MANAGEMENT_I18N_PREFIX,
    MODEL_VERSION_TABLE_SCROLL_X,
} from './pageConfig'

interface UploadSectionProps {
    canUpload: boolean
    columns: TableColumnsType<ModelVersionSummary>
    loading: boolean
    onOpenUpload: () => void
    rows: ModelVersionSummary[]
}

interface ConfigurationSectionProps {
    activeScopeTab: ModelScopeTabKey
    batchLoading: boolean
    columns: TableColumnsType<ModelConfigurationRow>
    creatingProjectConfig: boolean
    hasProjectConfigRecords: boolean
    loading: boolean
    onBatchAction: (
        action: 'change' | 'pause' | 'resume' | 'delete'
    ) => void
    onOpenCreateProjectConfig: () => void
    onProjectSearch: () => void
    onProjectSearchChange: (value: string) => void
    onProjectSearchReset: () => void
    onScopeTabChange: (value: ModelScopeTabKey) => void
    onSelectedProjectIdsChange: (keys: React.Key[]) => void
    projectColumns: TableColumnsType<ProjectConfigRecordRow>
    projectRows: ProjectConfigRecordRow[]
    projectSearch: string
    selectedProjectIds: React.Key[]
    rows: ModelConfigurationRow[]
}

interface BindingSectionProps {
    activeScopeTab: ModelScopeTabKey
    canManageCategories?: boolean
    columns: TableColumnsType<DisplayCategoryRow>
    creatingProjectConfig: boolean
    hasProjectConfigRecords: boolean
    loading: boolean
    onOpenCreateCategory?: () => void
    onOpenCreateProjectConfig: () => void
    onProjectSearch: () => void
    onProjectSearchChange: (value: string) => void
    onProjectSearchReset: () => void
    onScopeTabChange: (value: ModelScopeTabKey) => void
    projectColumns: TableColumnsType<ProjectConfigRecordRow>
    projectRows: ProjectConfigRecordRow[]
    projectSearch: string
    rows: DisplayCategoryRow[]
}

interface ScopeControlsProps {
    activeScopeTab: ModelScopeTabKey
    onScopeTabChange: (value: ModelScopeTabKey) => void
}

interface ProjectSearchControlsProps {
    onChange: (value: string) => void
    onReset: () => void
    onSearch: () => void
    value: string
}

export const ScopeControls: React.FC<ScopeControlsProps> = ({
    activeScopeTab,
    onScopeTabChange,
}) => {
    const { t } = useTranslation(['admin'])
    const scopeOptions = [
        {
            key: 'platform',
            label: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.scope.platformDefault`),
        },
        {
            key: 'project',
            label: t(`${MODEL_MANAGEMENT_I18N_PREFIX}.scope.projectConfig`),
        },
    ]

    return (
        <FilterBar data-testid="model-management-scope-filter-panel">
            <FilterField
                label={t(`${MODEL_MANAGEMENT_I18N_PREFIX}.scope.selectorLabel`)}
                testId="model-management-scope-filter-field"
            >
                <Radio.Group
                    buttonStyle="solid"
                    onChange={(event) =>
                        onScopeTabChange(event.target.value as ModelScopeTabKey)
                    }
                    value={activeScopeTab}
                >
                    {scopeOptions.map((option) => (
                        <Radio.Button key={option.key} value={option.key}>
                            {option.label}
                        </Radio.Button>
                    ))}
                </Radio.Group>
            </FilterField>
        </FilterBar>
    )
}

export const ProjectSearchControls: React.FC<ProjectSearchControlsProps> = ({
    onChange,
    onReset,
    onSearch,
    value,
}) => {
    const { t } = useTranslation(['admin'])

    return (
        <FilterBar data-testid="model-management-project-search-filter-panel">
            <FilterField
                label={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.filters.projectSearchLabel`
                )}
                testId="model-management-project-search-filter-field"
            >
                <Input
                    allowClear
                    aria-label={t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.filters.projectSearch`
                    )}
                    onChange={(event) => onChange(event.target.value)}
                    onPressEnter={onSearch}
                    placeholder={t(
                        `${MODEL_MANAGEMENT_I18N_PREFIX}.filters.projectSearch`
                    )}
                    value={value}
                />
            </FilterField>
            <FilterActions
                align="left"
                testId="model-management-project-search-actions"
            >
                <Button
                    icon={<SearchOutlined />}
                    onClick={onSearch}
                    type="primary"
                >
                    {t(`${MODEL_MANAGEMENT_I18N_PREFIX}.actions.search`)}
                </Button>
                <Button icon={<ReloadOutlined />} onClick={onReset}>
                    {t(`${MODEL_MANAGEMENT_I18N_PREFIX}.actions.reset`)}
                </Button>
            </FilterActions>
        </FilterBar>
    )
}

export const ProjectConfigEmpty: React.FC<{
    activeScopeTab: ModelScopeTabKey
    hasProjectConfigRecords: boolean
    hasProjectSearchResults: boolean
}> = ({ activeScopeTab, hasProjectConfigRecords, hasProjectSearchResults }) => {
    const { t } = useTranslation(['admin'])
    if (activeScopeTab !== 'project') {
        return null
    }
    if (!hasProjectConfigRecords) {
        return (
            <Empty
                description={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.empty.noProjectConfigRecords`
                )}
            />
        )
    }
    if (!hasProjectSearchResults) {
        return (
            <Empty
                description={t(
                    `${MODEL_MANAGEMENT_I18N_PREFIX}.empty.noProjectSearchResults`
                )}
            />
        )
    }
    return null
}

export const ModelUploadSection: React.FC<UploadSectionProps> = ({
    canUpload,
    columns,
    loading,
    onOpenUpload,
    rows,
}) => {
    const { t } = useTranslation(['admin'])

    return (
        <section>
            <PaginatedListContainer>
                <PaginatedListToolbar data-testid="model-management-upload-list-toolbar">
                    {canUpload ? (
                        <Button
                            icon={<UploadOutlined />}
                            onClick={onOpenUpload}
                            type="primary"
                        >
                            {t(`${MODEL_MANAGEMENT_I18N_PREFIX}.actions.upload`)}
                        </Button>
                    ) : null}
                </PaginatedListToolbar>
                <PaginatedListCard>
                    <Table
                        rowKey="id"
                        loading={loading}
                        dataSource={rows}
                        columns={columns}
                        size={PAGINATED_LIST_TABLE_SIZE}
                        pagination={buildPaginatedListPagination({
                            defaultPageSize: 10,
                            showTotal: (total) =>
                                t(`${MODEL_MANAGEMENT_I18N_PREFIX}.pagination.total`, {
                                    total,
                                }),
                        })}
                        scroll={{ x: MODEL_VERSION_TABLE_SCROLL_X }}
                    />
                </PaginatedListCard>
            </PaginatedListContainer>
        </section>
    )
}

export const ModelConfigurationSection: React.FC<ConfigurationSectionProps> = ({
    activeScopeTab,
    columns,
    creatingProjectConfig,
    hasProjectConfigRecords,
    loading,
    onOpenCreateProjectConfig,
    onProjectSearch,
    onProjectSearchReset,
    onScopeTabChange,
    projectColumns,
    projectRows,
    projectSearch,
    selectedProjectIds,
    onProjectSearchChange,
    onSelectedProjectIdsChange,
    onBatchAction,
    batchLoading,
    rows,
}) => {
    const { t } = useTranslation(['admin'])
    const showProjectEmpty = activeScopeTab === 'project' && projectRows.length === 0

    return (
        <section>
            <ScopeControls
                activeScopeTab={activeScopeTab}
                onScopeTabChange={onScopeTabChange}
            />
            {activeScopeTab === 'project' ? (
                <ProjectSearchControls
                    onChange={onProjectSearchChange}
                    onReset={onProjectSearchReset}
                    onSearch={onProjectSearch}
                    value={projectSearch}
                />
            ) : null}
            <PaginatedListContainer>
                <PaginatedListToolbar data-testid="model-management-configuration-list-toolbar">
                    {activeScopeTab === 'project' ? (
                        <>
                            <Button
                                icon={<PlusOutlined />}
                                loading={creatingProjectConfig}
                                onClick={onOpenCreateProjectConfig}
                                type="primary"
                            >
                                {t(`${MODEL_MANAGEMENT_I18N_PREFIX}.actions.createProjectConfig`)}
                            </Button>
                            <Space size={4} wrap>
                                {(
                                    [
                                        'change',
                                        'pause',
                                        'resume',
                                        'delete',
                                    ] as const
                                ).map((action) => (
                                    <Button
                                        danger={action === 'delete'}
                                        disabled={
                                            selectedProjectIds.length === 0
                                        }
                                        key={action}
                                        loading={batchLoading}
                                        onClick={() => onBatchAction(action)}
                                    >
                                        {t(
                                            `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.batch.${action}`,
                                        )}
                                    </Button>
                                ))}
                            </Space>
                        </>
                    ) : null}
                </PaginatedListToolbar>
                <PaginatedListCard>
                    {showProjectEmpty ? (
                        <ProjectConfigEmpty
                            activeScopeTab={activeScopeTab}
                            hasProjectConfigRecords={hasProjectConfigRecords}
                            hasProjectSearchResults={projectRows.length > 0}
                        />
                    ) : activeScopeTab === 'project' ? (
                        <Table
                            rowKey="projectId"
                            loading={loading}
                            dataSource={projectRows}
                            columns={projectColumns}
                            size={PAGINATED_LIST_TABLE_SIZE}
                            pagination={buildPaginatedListPagination({
                                defaultPageSize: 10,
                                showTotal: (total) =>
                                    t(`${MODEL_MANAGEMENT_I18N_PREFIX}.pagination.total`, {
                                        total,
                                    }),
                            })}
                            scroll={{ x: MODEL_CONFIGURATION_TABLE_SCROLL_X }}
                            rowSelection={{
                                selectedRowKeys: selectedProjectIds,
                                onChange: onSelectedProjectIdsChange,
                            }}
                        />
                    ) : (
                        <Table
                            rowKey="modelKey"
                            loading={loading}
                            dataSource={rows}
                            columns={columns}
                            size={PAGINATED_LIST_TABLE_SIZE}
                            pagination={false}
                            scroll={{ x: MODEL_CONFIGURATION_TABLE_SCROLL_X }}
                        />
                    )}
                </PaginatedListCard>
            </PaginatedListContainer>
        </section>
    )
}

export const ModelBindingSection: React.FC<BindingSectionProps> = ({
    activeScopeTab,
    canManageCategories = false,
    columns,
    creatingProjectConfig,
    hasProjectConfigRecords,
    loading,
    onOpenCreateCategory,
    onOpenCreateProjectConfig,
    onProjectSearchChange,
    onProjectSearch,
    onProjectSearchReset,
    onScopeTabChange,
    projectColumns,
    projectRows,
    projectSearch,
    rows,
}) => {
    const { t } = useTranslation(['admin'])
    const showProjectEmpty = activeScopeTab === 'project' && projectRows.length === 0

    return (
        <section>
            <ScopeControls
                activeScopeTab={activeScopeTab}
                onScopeTabChange={onScopeTabChange}
            />
            {activeScopeTab === 'project' ? (
                <ProjectSearchControls
                    onChange={onProjectSearchChange}
                    onReset={onProjectSearchReset}
                    onSearch={onProjectSearch}
                    value={projectSearch}
                />
            ) : null}
            <PaginatedListContainer>
                <PaginatedListToolbar data-testid="model-management-types-list-toolbar">
                    {activeScopeTab === 'platform' && canManageCategories ? (
                        <Button
                            icon={<PlusOutlined />}
                            onClick={onOpenCreateCategory}
                            type="primary"
                        >
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.createCategory`
                            )}
                        </Button>
                    ) : null}
                    {activeScopeTab === 'project' ? (
                        <Button
                            icon={<PlusOutlined />}
                            loading={creatingProjectConfig}
                            onClick={onOpenCreateProjectConfig}
                            type="primary"
                        >
                            {t(
                                `${MODEL_MANAGEMENT_I18N_PREFIX}.actions.createProjectConfig`
                            )}
                        </Button>
                    ) : null}
                </PaginatedListToolbar>
                <PaginatedListCard>
                    {showProjectEmpty ? (
                        <ProjectConfigEmpty
                            activeScopeTab={activeScopeTab}
                            hasProjectConfigRecords={hasProjectConfigRecords}
                            hasProjectSearchResults={projectRows.length > 0}
                        />
                    ) : activeScopeTab === 'project' ? (
                        <Table
                            rowKey="projectId"
                            loading={loading}
                            dataSource={projectRows}
                            columns={projectColumns}
                            size={PAGINATED_LIST_TABLE_SIZE}
                            pagination={buildPaginatedListPagination({
                                defaultPageSize: 10,
                                showTotal: (total) =>
                                    t(`${MODEL_MANAGEMENT_I18N_PREFIX}.pagination.total`, {
                                        total,
                                    }),
                            })}
                            scroll={{ x: BINDING_TABLE_SCROLL_X }}
                        />
                    ) : (
                        <Table
                            rowKey="key"
                            loading={loading}
                            dataSource={rows}
                            columns={columns}
                            size={PAGINATED_LIST_TABLE_SIZE}
                            pagination={buildPaginatedListPagination({
                                defaultPageSize: 10,
                                showTotal: (total) =>
                                    t(`${MODEL_MANAGEMENT_I18N_PREFIX}.pagination.total`, {
                                        total,
                                    }),
                            })}
                            scroll={{ x: BINDING_TABLE_SCROLL_X }}
                        />
                    )}
                </PaginatedListCard>
            </PaginatedListContainer>
        </section>
    )
}
